import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiUsers, FiCheck, FiAlertCircle, FiSearch } from 'react-icons/fi';
import apiService from '../../services/api';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s;
  
  &:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const StudentList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--bg-secondary);
`;

const StudentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: var(--bg-hover);
  }
  
  &.selected {
    background: var(--primary-color);
    color: var(--text-inverse);
  }
`;

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StudentName = styled.span`
  font-weight: 500;
  font-size: 0.875rem;
`;

const StudentEmail = styled.span`
  font-size: 0.75rem;
  opacity: 0.7;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  
  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    
    &:hover:not(:disabled) {
      background-color: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
  
  &.secondary {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background-color: var(--background-secondary);
      border-color: var(--border-dark);
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  color: var(--error-color);
  padding: 0.75rem;
  border-radius: 0;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(104, 211, 145, 0.1);
  border: 1px solid var(--success-color);
  color: var(--success-color);
  padding: 0.75rem;
  border-radius: 0;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SearchContainer = styled.div`
  margin-bottom: 1rem;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--background-secondary);
  }
`;

const SearchResults = styled.div`
  margin-bottom: 0.5rem;
  color: var(--text-muted);
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EmptySearchState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: var(--text-muted);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 0.5rem;
    opacity: 0.5;
  }
  
  h4 {
    color: var(--text-primary);
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }
  
  p {
    font-size: 0.875rem;
    margin: 0;
  }
`;

const EnrolledBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background-color: rgba(104, 211, 145, 0.1);
  color: var(--success-dark);
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StudentItemDisabled = styled(StudentItem)`
  opacity: 0.6;
  cursor: not-allowed;
  
  &:hover {
    background: var(--bg-secondary);
    border-color: var(--border-color);
  }
`;

function AssignElectiveStudentModal({ isOpen, onClose, elective, onStudentAssigned }) {
  const [students, setStudents] = useState([]);
  const [enrolledStudentIds, setEnrolledStudentIds] = useState(new Set());
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers();
      if (response.success) {
        // Filter only students
        const studentUsers = response.users.filter(user => user.role === 'student');
        setStudents(studentUsers);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEnrolledStudents = useCallback(async () => {
    if (!elective?.id) return;
    
    try {
      const response = await apiService.getElective(elective.id);
      if (response.success && response.elective.students) {
        const enrolledIds = new Set(response.elective.students.map(student => student.id));
        setEnrolledStudentIds(enrolledIds);
      }
    } catch (error) {
      console.error('Error fetching enrolled students:', error);
    }
  }, [elective?.id]);

  useEffect(() => {
    if (isOpen) {
      fetchStudents();
      fetchEnrolledStudents();
    }
  }, [isOpen, elective, fetchStudents, fetchEnrolledStudents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStudent) {
      setError('Please select a student');
      return;
    }

    if (enrolledStudentIds.has(selectedStudent.id)) {
      setError('This student is already enrolled in the elective');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await apiService.assignStudentToElective(elective.id, selectedStudent.id);
      
      if (response.success) {
        setSuccess('Student successfully assigned to elective');
        onStudentAssigned(selectedStudent);
        
        // Reset form
        setSelectedStudent(null);
        
        // Close modal after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(response.error || 'Failed to assign student to elective');
      }
    } catch (error) {
      console.error('Error assigning student:', error);
      setError('Failed to assign student to elective');
    } finally {
      setLoading(false);
    }
  };

  // Filter students based on search term
  const filteredStudents = useMemo(() => {
    if (!searchTerm.trim()) {
      return students;
    }
    
    const term = searchTerm.toLowerCase().trim();
    return students.filter(student => {
      const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
      const email = student.email.toLowerCase();
      return fullName.includes(term) || email.includes(term);
    });
  }, [students, searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleClose = () => {
    setSelectedStudent(null);
    setError('');
    setSuccess('');
    setSearchTerm('');
    setEnrolledStudentIds(new Set());
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <FiUsers size={20} />
                Assign Student to Elective
              </ModalTitle>
              <CloseButton onClick={handleClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Elective</Label>
                <div style={{ 
                  padding: '0.75rem', 
                  background: 'var(--bg-secondary)', 
                  borderRadius: 0,
                  border: '1px solid var(--border-color)'
                }}>
                  <strong>{elective?.code}</strong> - {elective?.name}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Select Student</Label>
                {loading ? (
                  <div style={{ 
                    padding: '1rem', 
                    textAlign: 'center',
                    color: 'var(--text-secondary)'
                  }}>
                    <LoadingSpinner />
                    Loading students...
                  </div>
                ) : students.length === 0 ? (
                  <div style={{ 
                    padding: '1rem', 
                    textAlign: 'center',
                    color: 'var(--text-secondary)'
                  }}>
                    No students available
                  </div>
                ) : (
                  <>
                    <SearchContainer>
                      <SearchIcon>
                        <FiSearch size={16} />
                      </SearchIcon>
                      <SearchInput
                        type="text"
                        placeholder="Search students by name or email..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      {searchTerm && (
                        <ClearSearchButton onClick={clearSearch}>
                          <FiX size={16} />
                        </ClearSearchButton>
                      )}
                    </SearchContainer>
                    
                    {searchTerm && (
                      <SearchResults>
                        <FiUsers size={12} />
                        Showing {filteredStudents.length} of {students.length} students
                      </SearchResults>
                    )}

                    {filteredStudents.length === 0 && searchTerm ? (
                      <EmptySearchState>
                        <FiUsers />
                        <h4>No Students Found</h4>
                        <p>No students match your search criteria.</p>
                      </EmptySearchState>
                    ) : (
                      <StudentList>
                        {filteredStudents.map(student => {
                          const isEnrolled = enrolledStudentIds.has(student.id);
                          const isSelected = selectedStudent?.id === student.id;
                          const StudentItemComponent = isEnrolled ? StudentItemDisabled : StudentItem;
                          
                          return (
                            <StudentItemComponent
                              key={student.id}
                              className={isSelected ? 'selected' : ''}
                              onClick={() => !isEnrolled && setSelectedStudent(student)}
                            >
                              <StudentInfo>
                                <StudentName>
                                  {student.first_name} {student.last_name}
                                </StudentName>
                                <StudentEmail>{student.email}</StudentEmail>
                              </StudentInfo>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {isEnrolled ? (
                                  <EnrolledBadge>
                                    <FiCheck size={12} />
                                    Enrolled
                                  </EnrolledBadge>
                                ) : isSelected ? (
                                  <FiCheck size={16} />
                                ) : null}
                              </div>
                            </StudentItemComponent>
                          );
                        })}
                      </StudentList>
                    )}
                  </>
                )}
              </FormGroup>

              {error && (
                <ErrorMessage
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <FiAlertCircle size={16} />
                  {error}
                </ErrorMessage>
              )}

              {success && (
                <SuccessMessage
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <FiCheck size={16} />
                  {success}
                </SuccessMessage>
              )}

              <ButtonGroup>
                <Button type="button" className="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="primary" 
                  disabled={loading || !selectedStudent}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <FiCheck size={16} />
                      Assign Student
                    </>
                  )}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default AssignElectiveStudentModal;

